import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import useClickOutside from '../../hooks/useClickOutside';

interface TownshipDeliveryPrice {
  id: number;
  township_name: string;
  township_name_mm?: string;
  state_region: string;
  state_region_mm?: string;
  delivery_price: number;
  estimated_days: number;
  is_active: boolean;
  notes?: string;
}

interface TownshipDeliveryModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode: 'create' | 'edit' | 'view';
  township?: TownshipDeliveryPrice | null;
  onSave: () => void;
}

interface FormData {
  township_name: string;
  township_name_mm: string;
  state_region: string;
  state_region_mm: string;
  delivery_price: string;
  estimated_days: string;
  is_active: boolean;
  notes: string;
}

const TownshipDeliveryModal: React.FC<TownshipDeliveryModalProps> = ({
  isOpen,
  onClose,
  mode,
  township,
  onSave
}) => {
  const modalRef = useClickOutside(onClose);
  const [formData, setFormData] = useState<FormData>({
    township_name: '',
    township_name_mm: '',
    state_region: '',
    state_region_mm: '',
    delivery_price: '',
    estimated_days: '1',
    is_active: true,
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string[]>>({});

  useEffect(() => {
    if (isOpen) {
      if (township && (mode === 'edit' || mode === 'view')) {
        setFormData({
          township_name: township.township_name,
          township_name_mm: township.township_name_mm || '',
          state_region: township.state_region,
          state_region_mm: township.state_region_mm || '',
          delivery_price: township.delivery_price.toString(),
          estimated_days: township.estimated_days.toString(),
          is_active: township.is_active,
          notes: township.notes || ''
        });
      } else {
        setFormData({
          township_name: '',
          township_name_mm: '',
          state_region: '',
          state_region_mm: '',
          delivery_price: '',
          estimated_days: '1',
          is_active: true,
          notes: ''
        });
      }
      setErrors({});
    }
  }, [isOpen, township, mode]);

  const handleInputChange = (field: keyof FormData, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: [] }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (mode === 'view') return;

    setLoading(true);
    setErrors({});

    try {
      const token = localStorage.getItem('token');
      const baseUrl = process.env.REACT_APP_API_URL || 'http://139.59.98.59/api';
      const url = mode === 'create' 
        ? `${baseUrl}/admin/township-delivery-prices`
        : `${baseUrl}/admin/township-delivery-prices/${township?.id}`;
      
      const method = mode === 'create' ? 'POST' : 'PUT';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          township_name: formData.township_name,
          township_name_mm: formData.township_name_mm || null,
          state_region: formData.state_region,
          state_region_mm: formData.state_region_mm || null,
          delivery_price: parseFloat(formData.delivery_price),
          estimated_days: parseInt(formData.estimated_days),
          is_active: formData.is_active,
          notes: formData.notes || null
        }),
      });

      const data = await response.json();

      if (response.ok) {
        onSave();
        onClose();
      } else {
        if (data.errors) {
          setErrors(data.errors);
        } else {
          alert(data.message || 'An error occurred');
        }
      }
    } catch (error) {
      console.error('Error saving township:', error);
      alert('An error occurred while saving');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const getTitle = () => {
    switch (mode) {
      case 'create': return 'Add Township Delivery Price';
      case 'edit': return 'Edit Township Delivery Price';
      case 'view': return 'Township Delivery Price Details';
      default: return '';
    }
  };

  const isReadOnly = mode === 'view';

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

        <div ref={modalRef} className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
          <div className="absolute top-0 right-0 pt-4 pr-4">
            <button
              type="button"
              className="bg-white rounded-md text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              onClick={onClose}
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <div className="sm:flex sm:items-start">
            <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                {getTitle()}
              </h3>

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Township Name (English) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Township Name (English) *
                  </label>
                  <input
                    type="text"
                    value={formData.township_name}
                    onChange={(e) => handleInputChange('township_name', e.target.value)}
                    disabled={isReadOnly}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white transition-all duration-200 ease-in-out hover:border-gray-400 hover:shadow-sm focus:outline-none focus:border-gray-500 focus:ring-4 focus:ring-gray-100 focus:shadow-md active:scale-[0.998] disabled:bg-gray-50 disabled:text-gray-500"
                    placeholder="e.g., Yangon, Mandalay"
                    required
                  />
                  {errors.township_name && (
                    <p className="mt-1 text-sm text-red-600">{errors.township_name[0]}</p>
                  )}
                </div>

                {/* Township Name (Myanmar) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Township Name (Myanmar)
                  </label>
                  <input
                    type="text"
                    value={formData.township_name_mm}
                    onChange={(e) => handleInputChange('township_name_mm', e.target.value)}
                    disabled={isReadOnly}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white transition-all duration-200 ease-in-out hover:border-gray-400 hover:shadow-sm focus:outline-none focus:border-gray-500 focus:ring-4 focus:ring-gray-100 focus:shadow-md active:scale-[0.998] disabled:bg-gray-50 disabled:text-gray-500"
                    placeholder="e.g., ရန်ကုန်, မန္တလေး"
                  />
                  {errors.township_name_mm && (
                    <p className="mt-1 text-sm text-red-600">{errors.township_name_mm[0]}</p>
                  )}
                </div>

                {/* State/Region (English) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    State/Region (English) *
                  </label>
                  <input
                    type="text"
                    value={formData.state_region}
                    onChange={(e) => handleInputChange('state_region', e.target.value)}
                    disabled={isReadOnly}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white transition-all duration-200 ease-in-out hover:border-gray-400 hover:shadow-sm focus:outline-none focus:border-gray-500 focus:ring-4 focus:ring-gray-100 focus:shadow-md active:scale-[0.998] disabled:bg-gray-50 disabled:text-gray-500"
                    placeholder="e.g., Yangon Region, Mandalay Region"
                    required
                  />
                  {errors.state_region && (
                    <p className="mt-1 text-sm text-red-600">{errors.state_region[0]}</p>
                  )}
                </div>

                {/* State/Region (Myanmar) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    State/Region (Myanmar)
                  </label>
                  <input
                    type="text"
                    value={formData.state_region_mm}
                    onChange={(e) => handleInputChange('state_region_mm', e.target.value)}
                    disabled={isReadOnly}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white transition-all duration-200 ease-in-out hover:border-gray-400 hover:shadow-sm focus:outline-none focus:border-gray-500 focus:ring-4 focus:ring-gray-100 focus:shadow-md active:scale-[0.998] disabled:bg-gray-50 disabled:text-gray-500"
                    placeholder="e.g., ရန်ကုန်တိုင်းဒေသကြီး"
                  />
                  {errors.state_region_mm && (
                    <p className="mt-1 text-sm text-red-600">{errors.state_region_mm[0]}</p>
                  )}
                </div>

                {/* Delivery Price */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Delivery Price (Ks) *
                  </label>
                  <input
                    type="number"
                    value={formData.delivery_price}
                    onChange={(e) => handleInputChange('delivery_price', e.target.value)}
                    disabled={isReadOnly}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white transition-all duration-200 ease-in-out hover:border-gray-400 hover:shadow-sm focus:outline-none focus:border-gray-500 focus:ring-4 focus:ring-gray-100 focus:shadow-md active:scale-[0.998] disabled:bg-gray-50 disabled:text-gray-500"
                    placeholder="0"
                    min="0"
                    step="0.01"
                    required
                  />
                  {errors.delivery_price && (
                    <p className="mt-1 text-sm text-red-600">{errors.delivery_price[0]}</p>
                  )}
                </div>

                {/* Estimated Days */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Estimated Delivery Days *
                  </label>
                  <input
                    type="number"
                    value={formData.estimated_days}
                    onChange={(e) => handleInputChange('estimated_days', e.target.value)}
                    disabled={isReadOnly}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white transition-all duration-200 ease-in-out hover:border-gray-400 hover:shadow-sm focus:outline-none focus:border-gray-500 focus:ring-4 focus:ring-gray-100 focus:shadow-md active:scale-[0.998] disabled:bg-gray-50 disabled:text-gray-500"
                    placeholder="1"
                    min="1"
                    max="30"
                    required
                  />
                  {errors.estimated_days && (
                    <p className="mt-1 text-sm text-red-600">{errors.estimated_days[0]}</p>
                  )}
                </div>

                {/* Active Status */}
                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => handleInputChange('is_active', e.target.checked)}
                      disabled={isReadOnly}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50"
                    />
                    <span className="ml-2 text-sm font-medium text-gray-700">
                      Active
                    </span>
                  </label>
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => handleInputChange('notes', e.target.value)}
                    disabled={isReadOnly}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white transition-all duration-200 ease-in-out hover:border-gray-400 hover:shadow-sm focus:outline-none focus:border-gray-500 focus:ring-4 focus:ring-gray-100 focus:shadow-md active:scale-[0.998] disabled:bg-gray-50 disabled:text-gray-500"
                    placeholder="Additional notes about delivery to this township..."
                  />
                  {errors.notes && (
                    <p className="mt-1 text-sm text-red-600">{errors.notes[0]}</p>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    {mode === 'view' ? 'Close' : 'Cancel'}
                  </button>
                  {mode !== 'view' && (
                    <button
                      type="submit"
                      disabled={loading}
                      className="px-4 py-2 text-sm font-medium text-white bg-gray-500 border border-transparent rounded-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? 'Saving...' : (mode === 'create' ? 'Create' : 'Update')}
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TownshipDeliveryModal;
